import React, { useEffect, useState } from "react";
import { Select } from "antd";
import moment from "moment";
import toast from "react-hot-toast";

import { getAllServiceApi } from "../../../api/request/service";
import {
  GetDeletedFileApi,
  PermanentlyDeleteFileApi,
  RestoreFileApi,
} from "../../../api/request/restore/files";

import columns from "../../../columns/files/viewFile";
import SkeletonTable from "../../../components/table/SkeletonTable";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import UserAvatar from "../../../components/common/UserAvatar";
import Search from "../../../components/common/Search";
import GlobalTooltip from "../../../components/common/GlobalTooltip";
import Delete from "../../../assets/Delete";

const RestoreFiles = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [selectedService, setSelectedService] = useState("all");
  const [serviceList, setServiceList] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteMode, setDeleteMode] = useState("single");

  const getServiceList = async () => {
    try {
      const { data } = await getAllServiceApi();
      if (data?.status) {
        const services = data?.data?.map((item) => ({
          value: item?.id,
          label: item?.name,
        }));
        setServiceList([{ value: "all", label: "All Services" }, ...services]);
        setTotal(data?.total_record);
      }
    } catch (err) {}
  };

  const getDeletedFile = async () => {
    try {
      setIsLoading(true);
      const params = {
        ...(selectedService !== "all" && { service_type_id: selectedService }),
      };
      const { data } = await GetDeletedFileApi(params);
      if (data?.status) {
        setData(
          data?.data?.map((item) => ({
            ...item,
            userDetails: <UserAvatar data={item?.user} />,
            file: (
              <div className="font-bold highlight-text capitalize">
                {item?.service_name_slug}
              </div>
            ),
            submit: item?.is_submit ? "Yes" : "No",
            assignTo: item?.employee_name,
            startDate: item?.created_at
              ? moment(item?.created_at).format("lll")
              : "",
            action: (
              <div className="flex items-center gap-2">
                <GlobalTooltip title={"Restore File"}>
                  <button
                    onClick={() => {
                      setSelectedId(item?.id);
                      setIsOpen(true);
                    }}
                  >
                    <img src="/assets/icons/restore-file.svg" alt="Icon" />
                  </button>
                </GlobalTooltip>
                <GlobalTooltip title={"Delete permanently"}>
                  <button
                    onClick={() => {
                      setSelectedRowKeys([item?.id]);
                      setDeleteMode("single");
                      setIsDeleteOpen(true);
                    }}
                  >
                    <Delete color="#000" size={5} />
                  </button>
                </GlobalTooltip>
              </div>
            ),
          }))
        );
        setTotal(data?.total_record);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const restoreFile = async () => {
    try {
      setIsLoading(true);
      const params = { file_id: selectedId };
      const { data: response } = await RestoreFileApi(params);
      if (response?.status) {
        toast?.success(response?.message);
        setIsOpen(false);
        setData((prev) => prev?.filter((item) => item?.id !== selectedId));
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const permanentlyDeleteFile = async () => {
    try {
      setIsLoading(true);
      const params = { file_id: selectedRowKeys };
      const { data: response } = await PermanentlyDeleteFileApi(params);
      if (response?.status) {
        toast?.success(response?.message);
        setIsDeleteOpen(false);
        setData((prev) =>
          prev?.filter((item) => !selectedRowKeys?.includes(item?.id))
        );
        setSelectedRowKeys([]);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getServiceList();
  }, []);

  useEffect(() => {
    if (selectedService) getDeletedFile();
  }, [selectedService]);

  useEffect(() => {
    setFiltered(data);
  }, [data]);

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination?.current);
  };

  const updatedColumns = [...columns];
  updatedColumns?.splice(4, 0, {
    title: "Deleted By",
    dataIndex: "deleted_by",
    render: (_, record) => record?.deleted_by?.name || "--",
  });

  return (
    <div className="border bg-White rounded-xl pb-5">
      <div className="flex items-center justify-between p-5">
        <h2 className="title">Restore Files {total ? `(${total})` : null}</h2>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="md:w-auto w-40">
            <Search data={data} setFiltered={setFiltered} setTotal={setTotal} />
          </div>

          <Select
            placeholder="Select service"
            className="md:w-[200px] w-40"
            options={serviceList}
            value={selectedService}
            onChange={(value) => setSelectedService(value)}
          />
        </div>
      </div>

      <div className="flex items-center gap-3 px-5 mb-4">
        {selectedRowKeys?.length > 0 && (
          <button
            className="btn-primary !px-2 !py-1"
            onClick={() => {
              setDeleteMode("bulk");
              setIsDeleteOpen(true);
            }}
          >
            Delete Selected Files
          </button>
        )}
      </div>

      <SkeletonTable
        rowKey="id"
        columns={updatedColumns}
        data={filtered}
        isLoading={isLoading}
        pagination={{
          current: currentPage,
          total: total,
          pageSize: 10,
          showSizeChanger: false,
        }}
        handleTableChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
      />

      {/* Confirmation modal for Restore file */}
      <ConfirmationModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        message="Are you sure, you want to restore file?"
        onConfirm={restoreFile}
        isLoading={isLoading}
        loadingLabel="Restoring file..."
      />

      {/* Confirmation modal for Permanently Delete file */}
      <ConfirmationModal
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        message={
          deleteMode === "single"
            ? "Are you sure you want to permanently delete this file?"
            : `Are you sure you want to permanently delete ${selectedRowKeys?.length} files?`
        }
        onConfirm={permanentlyDeleteFile}
        isLoading={isLoading}
        loadingLabel="Deleting file..."
      />
    </div>
  );
};

export default RestoreFiles;
